import { formatDurationClockShort } from "@ui/format";
import { createSignal, type ParentProps, Show } from "solid-js";
import type { VideoEvidence } from "../../ChatEventTypes";
import { clipWindow, seekTargetAtTrackFraction } from "./clipSeek";
import "./SeekTrack.css";

const KEYBOARD_SEEK_STEP_SECONDS = 5;

/**
 * Makes the progress bar it wraps scrubbable, keeping the playhead inside the
 * clip's range of the full-video timeline.
 */
export const SeekTrack = (
	props: ParentProps<{
		clip: VideoEvidence;
		playbackProgressPercent: number;
		onSeek: (clipRelativeSeconds: number) => void;
	}>,
) => {
	const [hoverPercent, setHoverPercent] = createSignal<number>();
	const clipDuration = () => clipWindow(props.clip).clipDuration;
	const currentSeconds = () =>
		(props.playbackProgressPercent / 100) * clipDuration();

	const targetAt = (event: MouseEvent) => {
		const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
		const fraction =
			rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0;
		return seekTargetAtTrackFraction(props.clip, fraction);
	};

	const seekTo = (seconds: number) =>
		props.onSeek(Math.max(0, Math.min(clipDuration(), seconds)));

	let scrubbing = false;
	const endScrub = (event: PointerEvent) => {
		scrubbing = false;
		const track = event.currentTarget as HTMLElement;
		if (track.hasPointerCapture?.(event.pointerId)) {
			track.releasePointerCapture(event.pointerId);
		}
	};

	return (
		<div
			class="clip-seek"
			role="slider"
			tabIndex={0}
			aria-label="Seek"
			aria-valuemin={0}
			aria-valuemax={clipDuration()}
			aria-valuenow={currentSeconds()}
			aria-valuetext={`${formatDurationClockShort(currentSeconds())} of ${formatDurationClockShort(clipDuration())}`}
			onPointerDown={(event) => {
				if (event.button !== 0) {
					return;
				}
				event.stopPropagation();
				// Capture keeps the drag tracking once the pointer slips off the
				// thin track, so scrubbing doesn't stall partway.
				(event.currentTarget as HTMLElement).setPointerCapture?.(
					event.pointerId,
				);
				scrubbing = true;
				const target = targetAt(event);
				setHoverPercent(target.trackPercent);
				props.onSeek(target.clipRelativeSeconds);
			}}
			onPointerMove={(event) => {
				const target = targetAt(event);
				setHoverPercent(target.trackPercent);
				if (scrubbing) {
					props.onSeek(target.clipRelativeSeconds);
				}
			}}
			onPointerUp={endScrub}
			onPointerCancel={endScrub}
			onPointerLeave={() => {
				if (!scrubbing) {
					setHoverPercent(undefined);
				}
			}}
			onClick={(event) => event.stopPropagation()}
			onKeyDown={(event) => {
				const target = {
					ArrowLeft: currentSeconds() - KEYBOARD_SEEK_STEP_SECONDS,
					ArrowRight: currentSeconds() + KEYBOARD_SEEK_STEP_SECONDS,
					Home: 0,
					End: clipDuration(),
				}[event.key];
				if (target === undefined) {
					return;
				}
				event.preventDefault();
				event.stopPropagation();
				seekTo(target);
			}}
		>
			{props.children}
			<Show when={hoverPercent() !== undefined}>
				<div
					class="clip-seek__hover-line"
					style={{ left: `${hoverPercent()}%` }}
					aria-hidden="true"
				/>
			</Show>
		</div>
	);
};
